import type {
  KeystrokeEvent,
  DwellTime,
  FlightTime,
  OverlapIndex,
  CaptureConfig,
  CaptureState,
  CaptureAnomaly,
} from '@/types/keystroke';

const DEFAULT_CONFIG: CaptureConfig = {
  captureModifiers: false,
  microPauseThreshold: 500,
  detectPaste: true,
  detectAutofill: true,
  minEventsForExtraction: 10,
};

/** Generate a unique event ID */
function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Modifier keys to optionally ignore */
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock']);

/** Left-hand keys on QWERTY layout */
const LEFT_HAND_KEYS = new Set([
  'KeyQ', 'KeyW', 'KeyE', 'KeyR', 'KeyT',
  'KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG',
  'KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB',
  'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5',
  'Backquote', 'Tab', 'CapsLock', 'ShiftLeft', 'ControlLeft', 'AltLeft',
]);

export type CaptureEventHandler = (events: KeystrokeEvent[]) => void;
export type AnomalyHandler = (anomaly: CaptureAnomaly) => void;

/**
 * KeystrokeCapture — core class that records keydown/keyup events
 * using performance.now() for high-resolution timing. Computes
 * DwellTime, FlightTime, and OverlapIndex in real time.
 */
export class KeystrokeCapture {
  private config: CaptureConfig;
  private events: KeystrokeEvent[] = [];
  private keysDown: Map<string, KeystrokeEvent> = new Map();
  private dwellTimes: DwellTime[] = [];
  private flightTimes: FlightTime[] = [];
  private overlapIndices: OverlapIndex[] = [];
  private lastKeyUp: KeystrokeEvent | null = null;
  private lastKeyDown: KeystrokeEvent | null = null;
  private state: CaptureState = 'idle';
  private element: HTMLElement | null = null;
  private anomalies: CaptureAnomaly[] = [];

  private onEventsCallback: CaptureEventHandler | null = null;
  private onAnomalyCallback: AnomalyHandler | null = null;

  // Paste detection
  private lastInputTime = 0;
  private rapidInputCount = 0;
  private readonly RAPID_INPUT_THRESHOLD = 5; // ms between events that suggests paste
  private readonly RAPID_INPUT_BURST = 5; // consecutive rapid inputs to trigger

  constructor(config: Partial<CaptureConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
  }

  /** Attach capture to a DOM element */
  attach(element: HTMLElement): void {
    this.detach();
    this.element = element;
    element.addEventListener('keydown', this.handleKeyDown);
    element.addEventListener('keyup', this.handleKeyUp);
    if (this.config.detectPaste) {
      element.addEventListener('paste', this.handlePaste);
    }
  }

  /** Detach from current element */
  detach(): void {
    if (this.element) {
      this.element.removeEventListener('keydown', this.handleKeyDown);
      this.element.removeEventListener('keyup', this.handleKeyUp);
      this.element.removeEventListener('paste', this.handlePaste);
      this.element = null;
    }
  }

  /** Start capturing */
  start(): void {
    this.state = 'capturing';
  }

  /** Pause capturing */
  pause(): void {
    this.state = 'paused';
  }

  /** Resume capturing */
  resume(): void {
    this.state = 'capturing';
  }

  /** Stop capturing */
  stop(): void {
    this.state = 'completed';
  }

  /** Reset all data */
  reset(): void {
    this.events = [];
    this.keysDown.clear();
    this.dwellTimes = [];
    this.flightTimes = [];
    this.overlapIndices = [];
    this.lastKeyUp = null;
    this.lastKeyDown = null;
    this.anomalies = [];
    this.state = 'idle';
    this.rapidInputCount = 0;
  }

  /** Register event handler */
  onEvents(handler: CaptureEventHandler): void {
    this.onEventsCallback = handler;
  }

  /** Register anomaly handler */
  onAnomaly(handler: AnomalyHandler): void {
    this.onAnomalyCallback = handler;
  }

  /** Get current state */
  getState(): CaptureState {
    return this.state;
  }

  /** Get all events */
  getEvents(): KeystrokeEvent[] {
    return [...this.events];
  }

  /** Get computed dwell times */
  getDwellTimes(): DwellTime[] {
    return [...this.dwellTimes];
  }

  /** Get computed flight times */
  getFlightTimes(): FlightTime[] {
    return [...this.flightTimes];
  }

  /** Get computed overlap indices */
  getOverlapIndices(): OverlapIndex[] {
    return [...this.overlapIndices];
  }

  /** Get anomalies */
  getAnomalies(): CaptureAnomaly[] {
    return [...this.anomalies];
  }

  /** Get latest N flight time values for waveform visualization */
  getRecentFlightTimes(n: number = 30): number[] {
    return this.flightTimes.slice(-n).map((ft) => ft.upDown);
  }

  /** Determine hand for a key code */
  static getHand(code: string): 'left' | 'right' | 'unknown' {
    if (LEFT_HAND_KEYS.has(code)) return 'left';
    if (code.startsWith('Key') || code.startsWith('Digit')) return 'right';
    return 'unknown';
  }

  /** Handle keydown event */
  private handleKeyDown(e: KeyboardEvent): void {
    if (this.state !== 'capturing') return;

    // Skip modifier-only keys if configured
    if (!this.config.captureModifiers && MODIFIER_KEYS.has(e.key)) return;

    // Skip repeated keys (held down)
    if (e.repeat) return;

    const timestamp = performance.now();
    const event: KeystrokeEvent = {
      id: uid(),
      type: 'keydown',
      key: e.key,
      code: e.code,
      timestamp,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
    };

    this.events.push(event);
    this.keysDown.set(e.code, event);

    // Paste/autofill detection via rapid input
    if (this.config.detectPaste || this.config.detectAutofill) {
      const elapsed = timestamp - this.lastInputTime;
      if (elapsed < this.RAPID_INPUT_THRESHOLD) {
        this.rapidInputCount++;
        if (this.rapidInputCount >= this.RAPID_INPUT_BURST) {
          const anomaly: CaptureAnomaly = {
            type: 'autofill',
            timestamp,
            details: `Detected ${this.rapidInputCount} rapid inputs (${elapsed.toFixed(1)}ms apart)`,
          };
          this.anomalies.push(anomaly);
          this.onAnomalyCallback?.(anomaly);
        }
      } else {
        this.rapidInputCount = 0;
      }
      this.lastInputTime = timestamp;
    }

    // Compute Flight Time (Down-Down) from last keydown
    if (this.lastKeyDown) {
      const downDown = timestamp - this.lastKeyDown.timestamp;
      // Compute Flight Time (Up-Down) from last keyup
      const upDown = this.lastKeyUp
        ? timestamp - this.lastKeyUp.timestamp
        : downDown;

      const ft: FlightTime = {
        fromKey: this.lastKeyDown.key,
        toKey: e.key,
        upDown,
        downDown,
      };
      this.flightTimes.push(ft);

      // Compute Overlap if previous key is still held
      if (this.lastKeyDown && this.keysDown.has(this.lastKeyDown.code)) {
        const overlap: OverlapIndex = {
          fromKey: this.lastKeyDown.key,
          toKey: e.key,
          overlap: this.lastKeyUp
            ? this.lastKeyUp.timestamp - timestamp
            : 0,
        };
        // Positive overlap means the next key was pressed before the prev was released
        if (this.lastKeyUp) {
          overlap.overlap = -(timestamp - this.lastKeyUp.timestamp);
        }
        this.overlapIndices.push(overlap);
      }
    }

    this.lastKeyDown = event;
    this.onEventsCallback?.(this.events);
  }

  /** Handle keyup event */
  private handleKeyUp(e: KeyboardEvent): void {
    if (this.state !== 'capturing') return;
    if (!this.config.captureModifiers && MODIFIER_KEYS.has(e.key)) return;

    const timestamp = performance.now();
    const event: KeystrokeEvent = {
      id: uid(),
      type: 'keyup',
      key: e.key,
      code: e.code,
      timestamp,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
    };

    this.events.push(event);

    // Compute Dwell Time
    const downEvent = this.keysDown.get(e.code);
    if (downEvent) {
      const dt: DwellTime = {
        key: e.key,
        code: e.code,
        duration: timestamp - downEvent.timestamp,
        downAt: downEvent.timestamp,
        upAt: timestamp,
      };
      this.dwellTimes.push(dt);
      this.keysDown.delete(e.code);
    }

    this.lastKeyUp = event;
    this.onEventsCallback?.(this.events);
  }

  /** Handle paste event */
  private handlePaste(e: Event): void {
    const anomaly: CaptureAnomaly = {
      type: 'paste',
      timestamp: performance.now(),
      details: 'Clipboard paste detected',
    };
    this.anomalies.push(anomaly);
    this.onAnomalyCallback?.(anomaly);

    // Optionally prevent the paste
    e.preventDefault();
  }

  /** Destroy the capture instance */
  destroy(): void {
    this.detach();
    this.reset();
  }
}
